import { Modal, Form, Button, Row, Nav } from "react-bootstrap";
import * as yup from "yup";
import { FieldArray, FormikProvider, useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { confirmable, createConfirmation } from "react-confirm";
import { useBackendUrl, useEffectOnce } from "../../utils/hooks";
import moment from "moment";
import DateTime from "react-datetime";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import { description } from "platform";
import { FilesAttachments } from "../../utils/Attachments";
import { isEmpty } from "lodash";

export default function ContentPage({}) {
  const backendUrl = useBackendUrl();
  const [isLoading, setIsLoading] = useState(false);
  const [activeKey, setActiveKey] = useState("termsAndConditions");

  const initialValues = {
    returnPolicy: "",
    termsAndConditions: "",
    onlineStoreHomePage: [],
  };

  const makeSchema = activeKey =>
    yup.object({
      returnPolicy: yup.string(),
      termsAndConditions: yup.string(),
      onlineStoreHomePage:
        activeKey === "homePage"
          ? yup.array().of(
              yup.object({
                title: yup
                  .string()
                  .trim()
                  .required("Title is required"),
                description: yup
                  .string()
                  .trim()
                  .required("Description is required"),
                btnText: yup
                  .string()
                  .trim()
                  .required("Button text is required"),
                image: yup
                  .array()
                  .min(1, "At least one image is required")
                  .required("Image is required"),
              })
            )
          : yup.mixed().notRequired(),
    });

  const schema = useMemo(() => makeSchema(activeKey), [activeKey]);
  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async values => {
      console.log(activeKey === "homePage");

      if (activeKey === "homePage") {
        const formData = new FormData();
        const images = values?.onlineStoreHomePage?.map(item => item.image);

        formData.append("onlineStoreHomPage", JSON.stringify(values?.onlineStoreHomePage));

        for (let i = 0; i < images.length; i++) {
          formData.append(`${i}`, images[i][0]);
        }
        createOnlineStoreHomePageMutation.mutate(formData);

        return;
      }
      createContentPageMutation.mutate(values);
    },
  });

  useEffect(() => {
    if (activeKey === "homePage") formik.validateForm();
  }, [activeKey]);

  const fetchSetUpData = async () => {
    let response = await fetch(`${backendUrl}/api/company/get-content-page`, {
      method: "GET",
      headers: {
        Accept: "Application/json",
        "Content-Type": "Application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();

    if (data?.pages) {
      formik.setValues({
        returnPolicy: data.pages?.find(el => el.title === "Return Policy")?.content,
        termsAndConditions: data.pages?.find(el => el.title === "Terms and Conditions")?.content,
      });
    }

    if (data?.homePage && data?.homePage[0] && data?.homePage[0]?.content) {
      let homePage = JSON.parse(data?.homePage[0]?.content);
      homePage = homePage?.map(el => ({
        ...el,
        image: [{ systemFileName: el?.image[0], fileId: el?.image[0] }],
      }));
      formik.setFieldValue("onlineStoreHomePage", homePage);
      data.homePage = homePage;
    }

    // -------
    return data;
  };

  const { isFetching } = useQuery(["CONTENT_PAGE_SETUP"], () => fetchSetUpData(), {
    keepPreviousData: false,
  });

  const createHomepage = async payload => {
    let response = await fetch(`${backendUrl}/api/company/add-online-store-homepage`, {
      method: "POST",
      // credentials: "include",
      body: payload,
    });
    if (!response.ok) {
      response = await response.json();
      if (response.errors) formik.setErrors(response.errors);
      throw new Error(response.message);
    }
    const res = await response.json();
    return res;
  };
  const createContentPage = async payload => {
    let response = await fetch(`${backendUrl}/api/company/add-content-page`, {
      method: "POST",
      // credentials: "include",
      body: JSON.stringify(payload),
      headers: {
        Accept: "Application/json",
        "Content-Type": "Application/json",
      },
    });
    if (!response.ok) {
      response = await response.json();
      if (response.errors) formik.setErrors(response.errors);
      throw new Error(response.message);
    }
    const res = await response.json();
    return res;
  };
  const createContentPageMutation = useMutation(payload => createContentPage(payload), {
    onSuccess: ({ message, data }) => {
      toast.success(message);
    },
    onError: ({ message }) => {
      toast.error(`Unable to perform action: ${message}`);
    },
  });

  const createOnlineStoreHomePageMutation = useMutation(payload => createHomepage(payload), {
    onSuccess: ({ message }) => {
      toast.success(message);
    },
    onError: ({ message }) => {
      toast.error(`Unable to perform action: ${message}`);
    },
  });
  return (
    <>
      <div className="row container px-5">
        <div className="my-4">
          <h6>Online Store</h6>
        </div>
        <Form noValidate onSubmit={formik.handleSubmit}>
          <Nav
            variant="tabs"
            defaultActiveKey="termsAndConditions"
            onSelect={key => setActiveKey(key)}
            className="mb-3"
          >
            <Nav.Item>
              <Nav.Link eventKey="termsAndConditions">Terms and Conditons</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="returnPolicy">Return Policy</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="homePage">Home Page</Nav.Link>
            </Nav.Item>
          </Nav>

          {activeKey === "termsAndConditions" && (
            <Form.Group className="mb-3 pb-1">
              <Form.Label className="mb-1">Terms and Conditons</Form.Label>
              <ReactQuill
                theme="snow"
                className="notranslate"
                value={formik.values.termsAndConditions}
                onChange={text => formik.setFieldValue("termsAndConditions", text)}
              />
            </Form.Group>
          )}

          {activeKey === "returnPolicy" && (
            <Form.Group className="mb-3 pb-1">
              <Form.Label className="mb-1">Return Policy</Form.Label>
              <ReactQuill
                theme="snow"
                value={formik.values.returnPolicy}
                onChange={text => formik.setFieldValue("returnPolicy", text)}
              />
            </Form.Group>
          )}

          {activeKey === "homePage" && (
            <FormikProvider value={formik}>
              <div>
                <FieldArray
                  name="onlineStoreHomePage"
                  render={arrayHelpers => (
                    <div className="row">
                      {formik?.values?.onlineStoreHomePage?.map((el, index) => {
                        const errors = formik.errors.onlineStoreHomePage?.[index] || {};
                        const touched = formik.touched.onlineStoreHomePage?.[index] || {};
                        return (
                          <div className=" col-md-6" key={el?.id}>
                            <Form.Group className=" mb-3">
                              <div className="d-flex justify-content-between">
                                <Form.Label>Title</Form.Label>
                                <button
                                  type="button"
                                  title="remove"
                                  className="btn btn-sm btn-primary text-nowrap   btn-danger"
                                  onClick={() => arrayHelpers.remove(index)}
                                >
                                  Remove
                                </button>
                              </div>
                              <Form.Control
                                placeholder="Enter slide title"
                                type="text"
                                name={`onlineStoreHomePage[${index}].title`}
                                onChange={e =>
                                  formik.setFieldValue(
                                    `onlineStoreHomePage[${index}].title`,
                                    e?.target?.value
                                  )
                                }
                                value={formik?.values?.onlineStoreHomePage[index]?.title}
                                {...formik.getFieldProps(`onlineStoreHomePage[${index}].title`)}
                                isInvalid={
                                  !!(
                                    formik.touched.onlineStoreHomePage?.[index]?.title &&
                                    formik.errors.onlineStoreHomePage?.[index]?.title
                                  )
                                }
                              />
                              {touched.title && errors.title && (
                                <div className="text-danger small">{errors.title}</div>
                              )}
                            </Form.Group>

                            <Form.Group className=" mb-3">
                              <Form.Label>Button Text</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Enter description"
                                name={`onlineStoreHomePage[${index}].btnText`}
                                onChange={e =>
                                  formik.setFieldValue(
                                    `onlineStoreHomePage[${index}].btnText`,
                                    e?.target?.value
                                  )
                                }
                                value={formik?.values?.onlineStoreHomePage[index]?.btnText}
                                {...formik.getFieldProps(`onlineStoreHomePage[${index}].btnText`)}
                                isInvalid={
                                  !!(
                                    formik.touched.onlineStoreHomePage?.[index]?.btnText &&
                                    formik.errors.onlineStoreHomePage?.[index]?.btnText
                                  )
                                }
                              />
                              {touched.btnText && errors.btnText && (
                                <div className="text-danger small">{errors.btnText}</div>
                              )}
                            </Form.Group>
                            <Form.Group className=" mb-3">
                              <Form.Label>Description</Form.Label>
                              <Form.Control
                                as={"textarea"}
                                rows={5}
                                placeholder="Enter slide button text"
                                name={`onlineStoreHomePage[${index}].description`}
                                onChange={e =>
                                  formik.setFieldValue(
                                    `onlineStoreHomePage[${index}].description`,
                                    e?.target?.value
                                  )
                                }
                                value={formik?.values?.onlineStoreHomePage[index]?.description}
                                {...formik.getFieldProps(
                                  `onlineStoreHomePage[${index}].description`
                                )}
                                isInvalid={
                                  !!(
                                    formik.touched.onlineStoreHomePage?.[index]?.description &&
                                    formik.errors.onlineStoreHomePage?.[index]?.description
                                  )
                                }
                              />
                              {touched.description && errors.description && (
                                <div className="text-danger small">{errors.description}</div>
                              )}
                            </Form.Group>

                            <Form.Group className="form-mb align-items-center mb-3">
                              <Form.Label>Image</Form.Label>
                              <FilesAttachments
                                url={`${backendUrl}/api/items/view-file`}
                                files={formik?.values?.onlineStoreHomePage[index]?.image}
                                setFiles={files =>
                                  formik.setFieldValue(`onlineStoreHomePage[${index}].image`, files)
                                }
                                accept="image/*"
                                colWidth="12"
                              />
                              {touched.image && errors.image && (
                                <div className="text-danger small">{errors.image}</div>
                              )}
                            </Form.Group>
                          </div>
                        );
                      })}

                      <div className="d-flex justify-content-center px-4  mb-3">
                        <button
                          type="button"
                          className="btn btn-sm btn-primary text-nowrap"
                          onClick={() =>
                            arrayHelpers.push({
                              id:
                                Date.now().toString() +
                                Math.random()
                                  .toString(36)
                                  .substr(2, 5), // ✅ simple unique id
                              title: "",
                              description: "",
                              image: [],
                              btnText: "",
                            })
                          }
                        >
                          + Add
                        </button>
                      </div>
                    </div>
                  )}
                />
              </div>
            </FormikProvider>
          )}

          <Button
            disabled={
              createContentPageMutation.isLoading ||
              createOnlineStoreHomePageMutation.isLoading ||
              isFetching
            }
            variant="primary"
            className="p-2 px-4 mx-auto"
            type="submit"
          >
            {createContentPageMutation.isLoading || createOnlineStoreHomePageMutation.isLoading
              ? "Please wait…"
              : "Save"}
          </Button>
        </Form>
      </div>{" "}
    </>
  );
}
